// src/webideal-jobs/pages/Auth.jsx
// Full-page auth with Login (password + OTP), Register, and Google sign-in

import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import {
    Mail,
    Lock,
    Eye,
    EyeOff,
    User,
    Phone,
    Briefcase,
    GraduationCap,
    ArrowLeft,
    Shield,
    Users,
    Building2,
} from "lucide-react";
import { GoogleLogin } from "@react-oauth/google";
import { backendApi } from "@/api/backendApi";

import { useAuth } from "../../context/AuthContext";

export default function Auth() {
    const navigate = useNavigate();
    const location = useLocation();
    const { login, user: contextUser, loading: authLoading } = useAuth();

    // Redirect if already logged in, but ONLY after auth check completes
    // Requirement 3: if (!loading && !user) redirect, never redirect during loading
    useEffect(() => {
        if (!authLoading && contextUser) {
            navigate(contextUser.role === "employer" ? "/recruit/dashboard" : "/jobs/dashboard", { replace: true });
        }

        // Check for incoming state (from Register page)
        if (location.state?.view) {
            setView(location.state.view);
            if (location.state.email) setOtpEmail(location.state.email);
            if (location.state.message) setSuccess(location.state.message);
        }
    }, [navigate, location, contextUser, authLoading]);

    // View states: "login" | "register" | "otp-send" | "otp-verify" | "forgot"
    const [view, setView] = useState("login");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    // Login form
    const [loginEmail, setLoginEmail] = useState("");
    const [loginPassword, setLoginPassword] = useState("");

    // OTP form
    const [otpEmail, setOtpEmail] = useState("");
    const [otp, setOtp] = useState("");

    // Register form
    const [regForm, setRegForm] = useState({
        fullname: "",
        email: "",
        password: "",
        mobile: "",
        role: "jobseeker",
        work_status: "",
    });

    const clearMessages = () => {
        setError("");
        setSuccess("");
    };

    // ──────── LOGIN ────────
    const handleLogin = async (e) => {
        e.preventDefault();
        clearMessages();
        if (!loginEmail || !loginPassword) {
            setError("Please enter email and password");
            return;
        }
        setLoading(true);
        try {
            const data = await backendApi.auth.login(loginEmail, loginPassword);
            login(data);
            navigate(data.role === "employer" ? "/recruit/dashboard" : "/jobs/dashboard", { replace: true });
        } catch (err) {
            setError(err.message || "Login failed. Please check your credentials.");
        } finally {
            setLoading(false);
        }
    };

    // ──────── SEND OTP ────────
    const handleSendOTP = async (e) => {
        e.preventDefault();
        clearMessages();
        if (!otpEmail) {
            setError("Please enter your email");
            return;
        }
        setLoading(true);
        try {
            const result = await backendApi.auth.sendOTP(otpEmail);
            setSuccess("OTP sent to your email! Check your inbox.");
            // In dev mode, show OTP
            if (result.otp) {
                setSuccess(`OTP sent! Dev mode OTP: ${result.otp}`);
            }
            setView("otp-verify");
        } catch (err) {
            if (err.message?.includes("register")) {
                setError("No account found. Please register first.");
            } else {
                setError(err.message || "Failed to send OTP");
            }
        } finally {
            setLoading(false);
        }
    };

    // ────── VERIFY OTP ──────
    const handleVerifyOTP = async (e) => {
        e.preventDefault();
        clearMessages();
        if (!otp) {
            setError("Please enter the OTP");
            return;
        }
        setLoading(true);
        try {
            const data = await backendApi.auth.verifyOTP(otpEmail, otp);
            login(data);
            setSuccess("Verified! Redirecting...");
            setTimeout(() => {
                navigate(data.role === "employer" ? "/recruit/dashboard" : "/jobs/dashboard", { replace: true });
            }, 500);
        } catch (err) {
            setError(err.message || "Invalid OTP. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // ────── REGISTER ──────
    const handleRegister = async (e) => {
        e.preventDefault();
        clearMessages();
        if (!regForm.fullname || !regForm.email || !regForm.password || !regForm.mobile) {
            setError("Please fill all required fields");
            return;
        }
        if (regForm.password.length < 6) {
            setError("Password must be at least 6 characters");
            return;
        }
        setLoading(true);
        try {
            const data = await backendApi.auth.register({
                full_name: regForm.fullname,
                email: regForm.email,
                password: regForm.password,
                mobile: regForm.mobile,
                role: regForm.role,
                work_status: regForm.work_status,
            });
            login(data);
            navigate(data.role === "employer" ? "/recruit/dashboard" : "/jobs/dashboard", { replace: true });
        } catch (err) {
            setError(err.message || "Registration failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // ────── GOOGLE SIGN-IN ──────
    const handleGoogleSuccess = async (credentialResponse) => {
        clearMessages();
        setLoading(true);
        try {
            const token = credentialResponse.credential; // This IS the ID Token
            const data = await backendApi.auth.googleLogin(token);
            login(data);
            navigate(data.role === "employer" ? "/recruit/dashboard" : "/jobs/dashboard", { replace: true });
        } catch (err) {
            setError(err.message || "Google sign-in failed");
        } finally {
            setLoading(false);
        }
    };




    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex">

            {/* ════════════════════════════════════════ */}
            {/* LEFT PANEL, Branding */}
            {/* ════════════════════════════════════════ */}
            <div className="hidden lg:flex lg:w-[45%] bg-gradient-to-br from-[#1e3a5f] via-[#2557a7] to-[#3b82f6] p-12 flex-col justify-between relative overflow-hidden">
                {/* Background decoration */}
                <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-72 h-72 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />

                <div className="relative z-10">
                    <Link to="/jobs" className="flex items-center gap-3 mb-16">
                        <img src="/logofinalcopy.png" alt="WebIdeal" className="w-10 h-10 rounded-full object-cover" />
                        <div>
                            <span className="text-xl font-bold text-white">WebIdeal</span>
                            <p className="text-[9px] text-blue-200 -mt-1 font-medium tracking-wider uppercase">AI-SMART Hiring</p>
                        </div>
                    </Link>

                    <h1 className="text-4xl font-bold text-white mb-4 leading-tight">
                        Find Your Dream Job
                        <br />
                        <span className="text-blue-200">With WebIdeal</span>
                    </h1>
                    <p className="text-blue-100 text-lg mb-12 leading-relaxed">
                        India's leading job portal connecting talented professionals with top companies.
                    </p>

                    <div className="space-y-6">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center backdrop-blur-sm">
                                <Briefcase className="w-6 h-6 text-blue-200" />
                            </div>
                            <div>
                                <p className="text-white font-semibold">10,000+ Active Jobs</p>
                                <p className="text-blue-200 text-sm">From top companies across India</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center backdrop-blur-sm">
                                <Users className="w-6 h-6 text-blue-200" />
                            </div>
                            <div>
                                <p className="text-white font-semibold">5 Lakh+ Candidates</p>
                                <p className="text-blue-200 text-sm">Trusted by professionals nationwide</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center backdrop-blur-sm">
                                <Building2 className="w-6 h-6 text-blue-200" />
                            </div>
                            <div>
                                <p className="text-white font-semibold">2,000+ Companies</p>
                                <p className="text-blue-200 text-sm">Hiring actively on WebIdeal</p>
                            </div>
                        </div>
                    </div>
                </div>

                <p className="text-blue-200/60 text-xs relative z-10">
                    © 2026 WebIdeal Software Solutions. All rights reserved.
                </p>
            </div>

            {/* ════════════════════════════════════════ */}
            {/* RIGHT PANEL, Auth Form */}
            {/* ════════════════════════════════════════ */}
            <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
                <div className="w-full max-w-[440px]">

                    <div className="flex items-center gap-3 mb-8">
                        <Link to="/jobs" className="flex items-center gap-3">
                            <img src="/logofinal.png" alt="WebIdeal" className="w-9 h-9 rounded-full object-cover" />
                            <span className="text-xl font-bold text-gray-800">WebIdeal</span>
                        </Link>
                    </div>

                    {/* ──── LOGIN VIEW ──── */}
                    {view === "login" && (
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-1">Welcome Back</h2>
                            <p className="text-gray-500 mb-8">Login to access your account</p>

                            {error && (
                                <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3 mb-4">
                                    {error}
                                </div>
                            )}

                            <form onSubmit={handleLogin} className="space-y-4">
                                {/* Email */}
                                <div>
                                    <label className="text-sm font-medium text-gray-700 block mb-1.5">Email ID</label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type="email"
                                            value={loginEmail}
                                            onChange={(e) => setLoginEmail(e.target.value)}
                                            placeholder="Enter your email"
                                            className="w-full h-11 pl-10 pr-4 border border-gray-300 rounded-lg focus:border-[#2557a7] focus:ring-1 focus:ring-[#2557a7] outline-none text-sm"
                                        />
                                    </div>
                                </div>

                                {/* Password */}
                                <div>
                                    <label className="text-sm font-medium text-gray-700 block mb-1.5">Password</label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            value={loginPassword}
                                            onChange={(e) => setLoginPassword(e.target.value)}
                                            placeholder="Enter your password"
                                            className="w-full h-11 pl-10 pr-12 border border-gray-300 rounded-lg focus:border-[#2557a7] focus:ring-1 focus:ring-[#2557a7] outline-none text-sm"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                    <div className="text-right mt-1.5">
                                        <button
                                            type="button"
                                            onClick={() => { clearMessages(); setView("forgot"); }}
                                            className="text-xs text-[#2557a7] hover:underline font-medium"
                                        >
                                            Forgot Password?
                                        </button>
                                    </div>
                                </div>

                                {/* Login Button */}
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full h-11 bg-[#2557a7] hover:bg-[#1e4687] text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                                >
                                    {loading ? "Logging in..." : "Login"}
                                </button>
                            </form>

                            {/* OTP Login */}
                            <div className="text-center mt-4">
                                <button
                                    onClick={() => { clearMessages(); setView("otp-send"); }}
                                    className="text-sm text-[#2557a7] hover:underline font-medium flex items-center gap-1.5 mx-auto"
                                >
                                    <Shield className="w-4 h-4" />
                                    Use OTP to Login
                                </button>
                            </div>

                            {/* Divider */}
                            <div className="relative my-6">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-gray-200" />
                                </div>
                                <div className="relative flex justify-center">
                                    <span className="px-3 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 text-gray-400 text-sm">or</span>
                                </div>
                            </div>

                            {/* Google Sign-in */}
                            <div className="w-full flex justify-center">
                                <GoogleLogin
                                    onSuccess={handleGoogleSuccess}
                                    onError={() => setError("Google Login Failed")}
                                    useOneTap
                                    theme="outline"
                                    size="large"
                                    width="100%"
                                />
                            </div>
                            
                            {/* Register link */}
                            <p className="text-center mt-6 text-sm text-gray-500">
                                New to WebIdeal?{" "}
                                <button
                                    onClick={() => { clearMessages(); setView("register"); }}
                                    className="text-[#2557a7] font-semibold hover:underline"
                                >
                                    Register for Free
                                </button>
                            </p>
                        </div>
                    )}

                    {/* ──── FORGOT PASSWORD VIEW ──── */}
                    {view === "forgot" && (
                        <div>
                            <button onClick={() => { clearMessages(); setView("login"); }} className="flex items-center gap-1 text-gray-500 hover:text-gray-700 text-sm mb-6">
                                <ArrowLeft className="w-4 h-4" /> Back to Login
                            </button>

                            <h2 className="text-2xl font-bold text-gray-900 mb-1">Forgot Password</h2>
                            <p className="text-gray-500 mb-8">Enter your email to receive reset instructions</p>

                            {error && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3 mb-4">{error}</div>}
                            {success && <div className="bg-green-50 border border-green-200 text-green-600 text-sm rounded-lg px-4 py-3 mb-4">{success}</div>}

                            <form onSubmit={async (e) => {
                                e.preventDefault();
                                clearMessages();
                                if (!loginEmail) {
                                    setError("Please enter your registered email address");
                                    return;
                                }
                                setLoading(true);
                                try {
                                    await backendApi.auth.forgotPassword(loginEmail);
                                    alert("Reset link sent!");
                                    setSuccess("Instructions sent! Check your email.");
                                } catch (err) {
                                    setError("Something went wrong. Try again.");
                                } finally {
                                    setLoading(false);
                                }
                            }} className="space-y-4">
                                <div>
                                    <label className="text-sm font-medium text-gray-700 block mb-1.5">Email Address</label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type="email"
                                            value={loginEmail}
                                            onChange={(e) => setLoginEmail(e.target.value)}
                                            placeholder="Enter your registered email"
                                            className="w-full h-11 pl-10 pr-4 border border-gray-300 rounded-lg focus:border-[#2557a7] focus:ring-1 focus:ring-[#2557a7] outline-none text-sm"
                                        />
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full h-11 bg-[#2557a7] hover:bg-[#1e4687] text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                                >
                                    {loading ? "Sending Instructions..." : "Reset Password"}
                                </button>
                            </form>
                        </div>
                    )}

                    {/* ──── OTP SEND VIEW ──── */}
                    {view === "otp-send" && (
                        <div>
                            <button onClick={() => { clearMessages(); setView("login"); }} className="flex items-center gap-1 text-gray-500 hover:text-gray-700 text-sm mb-6">
                                <ArrowLeft className="w-4 h-4" /> Back to Login
                            </button>

                            <h2 className="text-2xl font-bold text-gray-900 mb-1">Login with OTP</h2>
                            <p className="text-gray-500 mb-8">We'll send a verification code to your email</p>

                            {error && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3 mb-4">{error}</div>}
                            {success && <div className="bg-green-50 border border-green-200 text-green-600 text-sm rounded-lg px-4 py-3 mb-4">{success}</div>}

                            <form onSubmit={handleSendOTP} className="space-y-4">
                                <div>
                                    <label className="text-sm font-medium text-gray-700 block mb-1.5">Email Address</label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type="email"
                                            value={otpEmail}
                                            onChange={(e) => setOtpEmail(e.target.value)}
                                            placeholder="Enter your registered email"
                                            className="w-full h-11 pl-10 pr-4 border border-gray-300 rounded-lg focus:border-[#2557a7] focus:ring-1 focus:ring-[#2557a7] outline-none text-sm"
                                        />
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full h-11 bg-[#2557a7] hover:bg-[#1e4687] text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                                >
                                    {loading ? "Sending OTP..." : "Send OTP"}
                                </button>
                            </form>
                        </div>
                    )}

                    {/* ──── OTP VERIFY VIEW ──── */}
                    {view === "otp-verify" && (
                        <div>
                            <button onClick={() => { clearMessages(); setView("otp-send"); }} className="flex items-center gap-1 text-gray-500 hover:text-gray-700 text-sm mb-6">
                                <ArrowLeft className="w-4 h-4" /> Change email
                            </button>

                            <h2 className="text-2xl font-bold text-gray-900 mb-1">Enter OTP</h2>
                            <p className="text-gray-500 mb-8">
                                We sent a 6-digit code to <span className="font-semibold text-gray-700">{otpEmail}</span>
                            </p>

                            {error && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3 mb-4">{error}</div>}
                            {success && <div className="bg-green-50 border border-green-200 text-green-600 text-sm rounded-lg px-4 py-3 mb-4">{success}</div>}

                            <form onSubmit={handleVerifyOTP} className="space-y-4">
                                <div>
                                    <label className="text-sm font-medium text-gray-700 block mb-1.5">OTP Code</label>
                                    <div className="relative">
                                        <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type="text"
                                            value={otp}
                                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                            placeholder="Enter 6-digit OTP"
                                            maxLength={6}
                                            className="w-full h-11 pl-10 pr-4 border border-gray-300 rounded-lg focus:border-[#2557a7] focus:ring-1 focus:ring-[#2557a7] outline-none text-sm tracking-[0.3em] text-center font-semibold text-lg"
                                        />
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading || otp.length < 6}
                                    className="w-full h-11 bg-[#2557a7] hover:bg-[#1e4687] text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                                >
                                    {loading ? "Verifying..." : "Verify & Login"}
                                </button>

                                <button
                                    type="button"
                                    onClick={handleSendOTP}
                                    className="w-full text-sm text-[#2557a7] hover:underline font-medium"
                                >
                                    Didn't receive OTP? Resend
                                </button>
                            </form>
                        </div>
                    )}

                    {/* ──── REGISTER VIEW ──── */}
                    {view === "register" && (
                        <div>
                            <button onClick={() => { clearMessages(); setView("login"); }} className="flex items-center gap-1 text-gray-500 hover:text-gray-700 text-sm mb-6">
                                <ArrowLeft className="w-4 h-4" /> Back to Login
                            </button>

                            <h2 className="text-2xl font-bold text-gray-900 mb-1">
                                Create Account
                                {regForm.role === "employer" && (
                                    <span className="text-indigo-600 text-lg ml-2">(Employer)</span>
                                )}
                            </h2>
                            <p className="text-gray-500 mb-6">Join India's #1 job platform</p>

                            {error && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3 mb-4">{error}</div>}

                            <form onSubmit={handleRegister} className="space-y-4">
                                {/* Full Name */}
                                <div>
                                    <label className="text-sm font-medium text-gray-700 block mb-1.5">Full Name *</label>
                                    <div className="relative">
                                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type="text"
                                            value={regForm.fullname}
                                            onChange={(e) => setRegForm({ ...regForm, fullname: e.target.value })}
                                            placeholder="Enter your full name"
                                            className="w-full h-11 pl-10 pr-4 border border-gray-300 rounded-lg focus:border-[#2557a7] focus:ring-1 focus:ring-[#2557a7] outline-none text-sm"
                                        />
                                    </div>
                                </div>

                                {/* Email */}
                                <div>
                                    <label className="text-sm font-medium text-gray-700 block mb-1.5">Email *</label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type="email"
                                            value={regForm.email}
                                            onChange={(e) => setRegForm({ ...regForm, email: e.target.value })}
                                            placeholder="Enter your email"
                                            className="w-full h-11 pl-10 pr-4 border border-gray-300 rounded-lg focus:border-[#2557a7] focus:ring-1 focus:ring-[#2557a7] outline-none text-sm"
                                        />
                                    </div>
                                </div>

                                {/* Password */}
                                <div>
                                    <label className="text-sm font-medium text-gray-700 block mb-1.5">Password *</label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            value={regForm.password}
                                            onChange={(e) => setRegForm({ ...regForm, password: e.target.value })}
                                            placeholder="Min. 6 characters"
                                            className="w-full h-11 pl-10 pr-12 border border-gray-300 rounded-lg focus:border-[#2557a7] focus:ring-1 focus:ring-[#2557a7] outline-none text-sm"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>

                                {/* Mobile */}
                                <div>
                                    <label className="text-sm font-medium text-gray-700 block mb-1.5">Mobile *</label>
                                    <div className="relative">
                                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                            type="tel"
                                            value={regForm.mobile}
                                            onChange={(e) => setRegForm({ ...regForm, mobile: e.target.value })}
                                            placeholder="+91 Mobile number"
                                            className="w-full h-11 pl-10 pr-4 border border-gray-300 rounded-lg focus:border-[#2557a7] focus:ring-1 focus:ring-[#2557a7] outline-none text-sm"
                                        />
                                    </div>
                                </div>

                                {/* Role selector (for jobseeker only show work status) */}
                                {regForm.role === "jobseeker" && (
                                    <div>
                                        <label className="text-sm font-medium text-gray-700 block mb-2">Work Status</label>
                                        <div className="grid grid-cols-2 gap-3">
                                            <button
                                                type="button"
                                                onClick={() => setRegForm({ ...regForm, work_status: "experienced" })}
                                                className={`border rounded-lg p-3 text-left transition-all ${regForm.work_status === "experienced"
                                                    ? "border-[#2557a7] bg-blue-50 ring-1 ring-[#2557a7]"
                                                    : "border-gray-200 hover:border-gray-300"
                                                    }`}
                                            >
                                                <Briefcase className={`w-5 h-5 mb-1 ${regForm.work_status === "experienced" ? "text-[#2557a7]" : "text-gray-400"}`} />
                                                <p className="text-sm font-semibold text-gray-800">Experienced</p>
                                                <p className="text-[11px] text-gray-400">Have work exp</p>
                                            </button>

                                            <button
                                                type="button"
                                                onClick={() => setRegForm({ ...regForm, work_status: "fresher" })}
                                                className={`border rounded-lg p-3 text-left transition-all ${regForm.work_status === "fresher"
                                                    ? "border-[#2557a7] bg-blue-50 ring-1 ring-[#2557a7]"
                                                    : "border-gray-200 hover:border-gray-300"
                                                    }`}
                                            >
                                                <GraduationCap className={`w-5 h-5 mb-1 ${regForm.work_status === "fresher" ? "text-[#2557a7]" : "text-gray-400"}`} />
                                                <p className="text-sm font-semibold text-gray-800">Fresher</p>
                                                <p className="text-[11px] text-gray-400">Student / Graduate</p>
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Register Button */}
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full h-11 bg-[#2557a7] hover:bg-[#1e4687] text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                                >
                                    {loading ? "Creating Account..." : "Register"}
                                </button>
                            </form>

                            {/* Google sign up */}
                            <div className="relative my-5">
                                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
                                <div className="relative flex justify-center">
                                    <span className="px-3 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 text-gray-400 text-sm">or</span>
                                </div>
                            </div>

                            {/* Google sign up */}
                            <div className="w-full flex justify-center">
                                <GoogleLogin
                                    onSuccess={handleGoogleSuccess}
                                    onError={() => setError("Google Login Failed")}
                                    useOneTap
                                    theme="outline"
                                    size="large"
                                    width="100%"
                                />
                            </div>

                            <p className="text-center mt-5 text-sm text-gray-500">
                                Already have an account?{" "}
                                <button onClick={() => { clearMessages(); setView("login"); }} className="text-[#2557a7] font-semibold hover:underline">
                                    Login
                                </button>
                            </p>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
}
